import {
  canadaWorldBankCountry,
  mexicoWorldBankCountry,
  unitedStatesWorldBankCountry,
  type WorldBankAPICountry,
  worldBankCountryData,
} from "shared/helpers/profile/address/country/worldBankCountryData";

type GetWorldBankCountryInformationParams = {
  /** The country as stored on an address, e.g. "United States", "US" or "USA". */
  country?: string;
  /**
   * When `true`, returns `undefined` if no country matches.
   * When omitted or `false`, an unmatched or blank country falls back to the United States,
   * which existing callers (sales tax, Billhighway, Prolydian) rely on.
   * Pass `true` when a wrong guess is worse than no answer, e.g. for display.
   */
  noFallback?: boolean;
};

/**
 * Looks up a country by name, alternate name, ISO3 code or ISO2 code (case-insensitive).
 * Falls back to the United States unless `noFallback` is `true`.
 */
export const getWorldBankCountryInformation = ({
  country,
  noFallback,
}: GetWorldBankCountryInformationParams): WorldBankAPICountry | undefined => {
  //We need to take a string input and find the country information from the country list
  //Could be "United States", "US", "USA".
  const input = country?.toLowerCase();
  const match = input
    ? [
        unitedStatesWorldBankCountry,
        canadaWorldBankCountry,
        mexicoWorldBankCountry,
        ...worldBankCountryData,
      ].find(
        (c) =>
          c.name.toLowerCase() === input ||
          c.alternateNames?.some((name) => name.toLowerCase() === input) ||
          c.iso3Code.toLowerCase() === input ||
          c.iso2Code.toLowerCase() === input,
      )
    : undefined;

  if (match || noFallback) {
    return match;
  }

  console.error(
    `Country ${country} not found in the country list returning United States`,
  );
  //This could be "The United States", "United States of America"
  return unitedStatesWorldBankCountry;
};
